/** Matches http(s) and www URLs including query strings, hyphens, and paths. */
const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*?>/i;
const ESCAPED_HTML_REGEX = /&lt;\/?[a-z]/i;

/** Trailing punctuation that belongs to the sentence, not the URL. */
const TRAILING_URL_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function stripHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();
}

export function decodeHtmlEntities(text: string): string {
  if (!text || (!text.includes('&lt;') && !text.includes('&gt;') && !text.includes('&amp;'))) {
    return text;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

export function isHtmlContent(text: string): boolean {
  if (!text) return false;
  const decoded = decodeHtmlEntities(text);
  return HTML_TAG_REGEX.test(text) || HTML_TAG_REGEX.test(decoded) || ESCAPED_HTML_REGEX.test(text);
}

export function prepareContentForDisplay(text: string): string {
  if (!text) return '';
  let prepared = text.trim();

  if (ESCAPED_HTML_REGEX.test(prepared) || prepared.includes('&amp;lt;')) {
    prepared = decodeHtmlEntities(prepared);
    if (prepared.includes('&lt;')) {
      prepared = decodeHtmlEntities(prepared);
    }
  }

  return prepared;
}

export function cleanEditorHtml(html: string): string {
  if (!html || !isHtmlContent(html)) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (
        name.startsWith('bis_') ||
        name === 'style' ||
        name === 'class' ||
        name === 'id' ||
        name.startsWith('data-')
      ) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
}

export function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
}

export function trimUrlMatch(match: string): string {
  return match.replace(TRAILING_URL_PUNCTUATION, '');
}

/**
 * Rejoin URLs that were split across line breaks (common in contentEditable / paste).
 */
export function mergeBrokenUrlLines(text: string): string {
  return text
    .replace(
      /(https?:\/\/[^\s\n]*?)\s*[\r\n]+\s*(\?[&=%\w.-]+[^\s\n]*)/gi,
      '$1$2'
    )
    .replace(
      /(https?:\/\/[^\s\n]*?-)\s*[\r\n]+\s*([a-zA-Z0-9][^\s\n]*)/gi,
      '$1$2'
    )
    .replace(
      /(https?:\/\/[^\s\n]+)\s*[\r\n]+\s*([/&=a-zA-Z0-9._%-]+)/gi,
      '$1$2'
    );
}

export function findUrlsInText(
  text: string
): Array<{ url: string; index: number; length: number }> {
  const results: Array<{ url: string; index: number; length: number }> = [];
  const regex = new RegExp(URL_REGEX.source, URL_REGEX.flags);

  for (const match of text.matchAll(regex)) {
    const raw = match[0];
    const index = match.index ?? 0;
    const url = trimUrlMatch(raw);
    if (url) {
      results.push({ url, index, length: raw.length });
    }
  }

  return results;
}

export function linkifyPlainText(text: string): string {
  if (!text) return '';

  const merged = mergeBrokenUrlLines(text);
  const matches = findUrlsInText(merged);

  if (matches.length === 0) {
    return escapeHtml(merged).replace(/\n/g, '<br>');
  }

  let result = '';
  let lastIndex = 0;

  matches.forEach(({ url, index, length }) => {
    result += escapeHtml(merged.slice(lastIndex, index)).replace(/\n/g, '<br>');
    const href = normalizeUrl(url);
    result += `<a href="${href}" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`;
    lastIndex = index + length;
  });

  result += escapeHtml(merged.slice(lastIndex)).replace(/\n/g, '<br>');
  return result;
}

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'UL',
  'OL',
  'LI',
  'A',
  'DIV',
  'SPAN',
]);

function sanitizeNode(node: Node, fragment: DocumentFragment): void {
  if (node.nodeType === Node.TEXT_NODE) {
    fragment.appendChild(node.cloneNode(true));
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const element = node as HTMLElement;
  const tag = element.tagName.toUpperCase();

  if (!ALLOWED_TAGS.has(tag)) {
    Array.from(element.childNodes).forEach((child) => sanitizeNode(child, fragment));
    return;
  }

  const clean = document.createElement(tag.toLowerCase());

  if (tag === 'A') {
    const href = normalizeUrl(element.getAttribute('href') || '');
    if (/^https?:\/\//i.test(href)) {
      clean.setAttribute('href', href);
      clean.setAttribute('target', '_blank');
      clean.setAttribute('rel', 'noopener noreferrer');
    }
  }

  Array.from(element.childNodes).forEach((child) => {
    const childFragment = document.createDocumentFragment();
    sanitizeNode(child, childFragment);
    clean.appendChild(childFragment);
  });

  fragment.appendChild(clean);
}

export function sanitizeRichTextHtml(html: string): string {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const output = document.createDocumentFragment();

  Array.from(doc.body.childNodes).forEach((node) => sanitizeNode(node, output));

  const container = document.createElement('div');
  container.appendChild(output);
  return container.innerHTML;
}

export function mergeBrokenUrlsInHtmlString(html: string): string {
  return html
    .replace(
      /(https?:\/\/[^<\s]*?)(?:<br\s*\/?>|\s*<\/div>\s*<div[^>]*>\s*)([?#/&][^<\s]*)/gi,
      '$1$2'
    )
    .replace(
      /(https?:\/\/[^<\s]*?-)(?:<br\s*\/?>|\s*<\/div>\s*<div[^>]*>\s*)([a-zA-Z0-9][^<\s]*)/gi,
      '$1$2'
    );
}

export function normalizeDescriptionContent(description: string): string {
  if (!description) return '';

  const prepared = prepareContentForDisplay(description);
  const cleaned = cleanEditorHtml(prepared);

  if (isHtmlContent(cleaned)) {
    return mergeBrokenUrlsInHtmlString(mergeBrokenUrlLines(cleaned));
  }
  return mergeBrokenUrlLines(cleaned);
}

function mergeBrokenUrlsInHtml(root: HTMLElement): void {
  const blocks = root.querySelectorAll('div, p, br');
  blocks.forEach((el) => {
    if (el.tagName === 'BR') return;

    const prev = el.previousElementSibling;
    if (!prev) return;

    const prevText = prev.textContent || '';
    const currText = el.textContent || '';
    if (!prevText || !currText) return;

    const endsWithPartialUrl = /https?:\/\/\S*?[-/]?$/i.test(prevText.trim());
    const continuesUrl = /^[?#/&=a-zA-Z0-9._%-]/.test(currText.trim());

    if (endsWithPartialUrl && continuesUrl) {
      prev.textContent = prevText + currText;
      el.remove();
    }
  });
}

function linkifyTextNodes(root: HTMLElement): void {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  let current = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const text = textNode.textContent || '';
    const matches = findUrlsInText(text);
    if (matches.length === 0) return;

    const parentElement = textNode.parentElement;
    if (!parentElement || parentElement.tagName === 'A') return;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    matches.forEach(({ url, index, length }) => {
      if (index > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, index))
        );
      }

      const anchor = document.createElement('a');
      anchor.href = normalizeUrl(url);
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.textContent = url;
      fragment.appendChild(anchor);
      lastIndex = index + length;
    });

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  });
}

export function formatTextForDisplay(text: string): string {
  const prepared = prepareContentForDisplay(text);
  if (!prepared) return '';

  if (isHtmlContent(prepared)) {
    const container = document.createElement('div');
    const normalized = normalizeDescriptionContent(prepared);
    container.innerHTML = sanitizeRichTextHtml(normalized);
    mergeBrokenUrlsInHtml(container);
    linkifyTextNodes(container);
    return container.innerHTML;
  }

  return linkifyPlainText(prepared);
}
